import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Building2,
  ArrowLeft,
  Pencil,
  User,
  Mail,
  Phone,
  MapPin,
  Globe,
  Link2,
  X,
} from "lucide-react";

const AUTH_HEADERS = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_admin_token") || ""}`,
});

const STATUS_MAP = {
  active: { label: "Activo", cls: "bg-green-100 text-green-700" },
  inactive: { label: "Inactivo", cls: "bg-gray-100 text-gray-500" },
  suspended: { label: "Suspendido", cls: "bg-red-100 text-red-600" },
  setup: { label: "Configurando", cls: "bg-yellow-100 text-yellow-700" },
};

export default function AdminClubs() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | create | detail
  const [selectedClub, setSelectedClub] = useState(null);

  const fetchClubs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/clubs`, {
        credentials: "include",
        headers: AUTH_HEADERS(),
      });
      if (res.ok) setClubs(await res.json());
    } catch {
      toast.error("Error al cargar clubes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClubs(); }, [fetchClubs]);

  const openDetail = async (clubId) => {
    try {
      const res = await fetch(`${API}/admin/clubs/${clubId}`, {
        credentials: "include",
        headers: AUTH_HEADERS(),
      });
      if (res.ok) {
        setSelectedClub(await res.json());
        setView("detail");
      }
    } catch {
      toast.error("Error al cargar club");
    }
  };

  if (view === "create") {
    return (
      <CreateClubView
        onBack={() => setView("list")}
        onCreated={() => { setView("list"); fetchClubs(); }}
      />
    );
  }

  if (view === "detail" && selectedClub) {
    return (
      <ClubDetailView
        club={selectedClub}
        onBack={() => { setView("list"); setSelectedClub(null); }}
        onUpdated={(updated) => { setSelectedClub(updated); fetchClubs(); }}
      />
    );
  }

  // --- List view ---
  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="admin-clubs-title">
            <Building2 className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
            Clubes
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Gestion de clubes y organizaciones</p>
        </div>
        <Button
          onClick={() => setView("create")}
          className="bg-[#1B4D3E] hover:bg-[#163F33] text-white"
          data-testid="create-club-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo Club
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : clubs.length === 0 ? (
        <Card className="card-default">
          <CardContent className="py-16 text-center">
            <Building2 className="w-12 h-12 text-[#64748B] mx-auto mb-4 opacity-40" strokeWidth={1.5} />
            <p className="text-[#64748B] font-medium mb-1">No hay clubes registrados</p>
            <p className="text-sm text-[#94A3B8] mb-4">Crea el primer club para empezar</p>
            <Button onClick={() => setView("create")} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white">
              <Plus className="w-4 h-4 mr-2" /> Crear Club
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-default">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="clubs-table">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Club</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Slug</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Cadiihouse Master</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Creado</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {clubs.map((club) => {
                    const st = STATUS_MAP[club.status] || STATUS_MAP.setup;
                    return (
                      <tr
                        key={club.club_id}
                        className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer"
                        onClick={() => openDetail(club.club_id)}
                        data-testid={`club-row-${club.club_id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#0F172A]">{club.name}</div>
                          {club.city && <div className="text-xs text-[#94A3B8]">{club.city}{club.country ? `, ${club.country}` : ""}</div>}
                        </td>
                        <td className="px-4 py-3 text-[#64748B] font-mono text-xs">/{club.slug}</td>
                        <td className="px-4 py-3 text-[#64748B]">
                          {club.master_user ? (
                            <div>
                              <div className="text-[#0F172A] text-sm">{club.master_user.name}</div>
                              <div className="text-xs text-[#94A3B8]">{club.master_user.email}</div>
                            </div>
                          ) : (
                            <span className="text-[#94A3B8]">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-4 py-3 text-[#64748B] text-xs">
                          {club.created_at ? new Date(club.created_at).toLocaleDateString("es-MX") : "-"}
                        </td>
                        <td className="px-4 py-3">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(club.club_id); }}>
                            <Pencil className="w-4 h-4 text-[#64748B]" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ==========================================================================
// Create Club View
// ==========================================================================

function CreateClubView({ onBack, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", short_name: "", slug: "", email: "", phone: "", city: "", country: "Mexico",
    master_name: "", master_email: "", master_phone: "", master_password: "",
  });

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Auto-generate slug from name
  const handleNameChange = (val) => {
    set("name", val);
    if (!form.slug || form.slug === slugify(form.name)) {
      set("slug", slugify(val));
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error("Nombre del club requerido");
    if (!form.slug.trim()) return toast.error("Slug requerido");
    if (!form.master_name.trim()) return toast.error("Nombre del master requerido");
    if (!form.master_email.trim()) return toast.error("Email del master requerido");
    if (!form.master_password) return toast.error("Contrasena del master requerida");

    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/clubs`, {
        method: "POST",
        credentials: "include",
        headers: AUTH_HEADERS(),
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error al crear");
      }
      toast.success("Club creado exitosamente");
      onCreated();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al crear club");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A] mb-6 transition-colors" data-testid="back-to-clubs">
        <ArrowLeft className="w-4 h-4" /> Volver a Clubes
      </button>

      <h1 className="text-2xl font-bold text-[#0F172A] mb-6" data-testid="create-club-title">Nuevo Club</h1>

      {/* Club info */}
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#1B4D3E]" /> Informacion del Club
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-sm font-medium">Nombre del Club *</Label>
              <Input value={form.name} onChange={(e) => handleNameChange(e.target.value)} placeholder="Guadalajara Country Club" data-testid="club-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Nombre Corto</Label>
              <Input value={form.short_name} onChange={(e) => set("short_name", e.target.value)} placeholder="GCC" data-testid="club-short-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Slug (URL) *</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#94A3B8]">cadii.app/</span>
                <Input value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="gcc" className="font-mono" data-testid="club-slug-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email del Club</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="info@club.com" data-testid="club-email-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Telefono</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+52 33 1234 5678" data-testid="club-phone-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Ciudad</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Guadalajara" data-testid="club-city-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Pais</Label>
              <Input value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="Mexico" data-testid="club-country-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Master user */}
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-[#1B4D3E]" /> Cadiihouse Master
          </h2>
          <p className="text-xs text-[#94A3B8] mb-4">El usuario principal que administrara este club</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-sm font-medium">Nombre *</Label>
              <Input value={form.master_name} onChange={(e) => set("master_name", e.target.value)} placeholder="Fernando Alarcon" data-testid="master-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Email *</Label>
              <Input type="email" value={form.master_email} onChange={(e) => set("master_email", e.target.value)} placeholder="fernando@club.com" data-testid="master-email-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Telefono</Label>
              <Input value={form.master_phone} onChange={(e) => set("master_phone", e.target.value)} placeholder="+52 33 1234 5678" data-testid="master-phone-input" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-sm font-medium">Contrasena *</Label>
              <Input type="password" value={form.master_password} onChange={(e) => set("master_password", e.target.value)} placeholder="Min. 6 caracteres" data-testid="master-password-input" />
              <p className="text-xs text-[#94A3B8]">El master podra cambiarla despues</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onBack}>Cancelar</Button>
        <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSubmit} disabled={saving} data-testid="save-club-btn">
          {saving ? "Creando..." : "Crear Club"}
        </Button>
      </div>
    </div>
  );
}


// ==========================================================================
// Club Detail / Edit View
// ==========================================================================

function ClubDetailView({ club, onBack, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: club.name || "",
    short_name: club.short_name || "",
    slug: club.slug || "",
    email: club.email || "",
    phone: club.phone || "",
    city: club.city || "",
    country: club.country || "",
    status: club.status || "active",
  });

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/clubs/${club.club_id}`, {
        method: "PUT",
        credentials: "include",
        headers: AUTH_HEADERS(),
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error");
      }
      const updated = await res.json();
      toast.success("Club actualizado");
      setEditing(false);
      onUpdated(updated);
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const st = STATUS_MAP[club.status] || STATUS_MAP.setup;

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A] mb-6 transition-colors" data-testid="back-to-clubs-detail">
        <ArrowLeft className="w-4 h-4" /> Volver a Clubes
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]" data-testid="club-detail-title">{club.name}</h1>
          <p className="text-sm text-[#64748B] font-mono">/{club.slug}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.cls}`}>{st.label}</span>
          {!editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-club-btn">
              <Pencil className="w-4 h-4 mr-1" /> Editar
            </Button>
          )}
        </div>
      </div>

      {/* Club info card */}
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#1B4D3E]" /> Informacion del Club
          </h2>
          {editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-sm font-medium">Nombre</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-club-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Nombre Corto</Label>
                <Input value={form.short_name} onChange={(e) => set("short_name", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Slug</Label>
                <Input value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} className="font-mono" data-testid="edit-club-slug" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Email</Label>
                <Input value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Telefono</Label>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Ciudad</Label>
                <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Pais</Label>
                <Input value={form.country} onChange={(e) => set("country", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="edit-club-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="inactive">Inactivo</SelectItem>
                    <SelectItem value="suspended">Suspendido</SelectItem>
                    <SelectItem value="setup">Configurando</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
              <InfoRow label="Nombre" value={club.name} />
              <InfoRow label="Nombre Corto" value={club.short_name} />
              <InfoRow label="Slug" value={`/${club.slug}`} mono />
              <InfoRow label="Email" value={club.email} />
              <InfoRow label="Telefono" value={club.phone} />
              <InfoRow label="Ciudad" value={club.city} />
              <InfoRow label="Pais" value={club.country} />
              <InfoRow label="Creado" value={club.created_at ? new Date(club.created_at).toLocaleDateString("es-MX") : "-"} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Master user card */}
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-[#1B4D3E]" /> Cadiihouse Master
          </h2>
          {club.master_user ? (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#1B4D3E] flex items-center justify-center text-white font-bold text-lg">
                {club.master_user.name?.[0] || "M"}
              </div>
              <div>
                <p className="font-medium text-[#0F172A]">{club.master_user.name}</p>
                <p className="text-sm text-[#64748B]">{club.master_user.email}</p>
                {club.master_user.phone && <p className="text-xs text-[#94A3B8]">{club.master_user.phone}</p>}
              </div>
            </div>
          ) : (
            <p className="text-[#94A3B8]">No asignado</p>
          )}
        </CardContent>
      </Card>

      {editing && (
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => setEditing(false)}>Cancelar</Button>
          <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSave} disabled={saving} data-testid="save-edit-club-btn">
            {saving ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </div>
      )}
    </div>
  );
}


// ==========================================================================
// Helpers
// ==========================================================================

function InfoRow({ label, value, mono }) {
  return (
    <div>
      <span className="text-[#94A3B8] text-xs uppercase">{label}</span>
      <p className={`text-[#0F172A] ${mono ? "font-mono text-xs" : ""}`}>{value || "-"}</p>
    </div>
  );
}

function slugify(str) {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
}
